import React, { useMemo } from 'react';
import type { SecurityEventItem } from '../../api-utils/security_events';
import type { ThreatEventItem } from '../../api-utils/threats';
import { isDuplicateEvent } from '../../api-utils/security_events';
import { formatUtcDateTime } from '../../lib/time';

interface IncidentHistoryProps {
  /** Single threat (incident dossier). Ignored when `threats` is non-empty. */
  threat?: ThreatEventItem | null;
  /** Campaign / multi-threat merge — Formed + windows per member, then shared SEs. */
  threats?: ThreatEventItem[];
  events: SecurityEventItem[];
  /** Optional label per threat_event _id (e.g. action name / short sha). */
  threatLabels?: Record<string, string>;
}

function eventMs(event: SecurityEventItem): number {
  const ms = Date.parse(event.event_time || event.ingestion_time || '');
  return Number.isFinite(ms) ? ms : 0;
}

function formatWhen(iso?: string): string {
  return formatUtcDateTime(iso, true);
}

function pushThreatMarkers(
  out: Array<{ id: string; label: string; when: string; note?: string }>,
  threat: ThreatEventItem,
  memberLabel?: string,
  seTimes?: Set<string>
) {
  const tag = memberLabel ? memberLabel : undefined;
  if (threat.created_at) {
    out.push({
      id: `${threat._id}:formed`,
      label: tag ? `Formed · ${tag}` : 'Formed',
      when: threat.created_at,
      note: tag ? 'When the member incident was created' : 'When the incident was created',
    });
  }
  if (threat.first_event_time && threat.first_event_time !== threat.created_at) {
    out.push({
      id: `${threat._id}:first`,
      label: tag ? `First event · ${tag}` : 'First event',
      when: threat.first_event_time,
      note: 'attack window start',
    });
  }
  if (
    threat.last_event_time &&
    threat.last_event_time !== threat.first_event_time &&
    !(seTimes && seTimes.has(threat.last_event_time))
  ) {
    out.push({
      id: `${threat._id}:last`,
      label: tag ? `Last event · ${tag}` : 'Last event',
      when: threat.last_event_time,
      note: 'attack window end',
    });
  }
}

const IncidentHistory: React.FC<IncidentHistoryProps> = ({
  threat,
  threats,
  events,
  threatLabels,
}) => {
  const rows = useMemo(() => {
    const out: Array<{ id: string; label: string; when: string; note?: string }> = [];
    const ordered = [...events].sort((a, b) => eventMs(a) - eventMs(b));
    const seTimes = new Set(
      ordered.map((event) => event.event_time || '').filter(Boolean)
    );

    const bundle =
      threats && threats.length > 0 ? threats : threat ? [threat] : [];

    for (const item of bundle) {
      pushThreatMarkers(out, item, threatLabels?.[item._id], seTimes);
    }

    for (const event of ordered) {
      const when = event.event_time || event.ingestion_time || '';
      if (!when) continue;
      out.push({
        id: event._id,
        label: String(event.event_type || event.action || 'security event'),
        when,
        note: isDuplicateEvent(event.is_duplicate) ? 'reobserved' : undefined,
      });
    }

    out.sort((a, b) => Date.parse(a.when) - Date.parse(b.when));
    return out;
  }, [threat, threats, events, threatLabels]);

  if (rows.length === 0) {
    return <p className="text-c3-mute text-sm">No timestamps on this threat yet.</p>;
  }

  return (
    <ol className="c3-history">
      {rows.map((row) => (
        <li key={row.id} className="c3-history-row">
          <div>
            <div className="font-medium">{row.label}</div>
            {row.note && <div className="text-[12px] text-c3-mute">{row.note}</div>}
          </div>
          <div className="text-[12px] text-c3-mute text-right font-mono">{formatWhen(row.when)}</div>
        </li>
      ))}
    </ol>
  );
};

export default IncidentHistory;
